import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpenItem, OpenItemList, OpenItemSource } from '@up2d8/contracts';
import { Card, LoadingState, cn, relativeTime } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { openItemLink } from './app-open-items';
import { DeskShell } from './desk/desk-shell';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';

/**
 * Het scherm "Mijn taken" (mijn-taken-replica-spec, SCH-6).
 *
 * Dezelfde lijst als de kaart "Voor jou" op het beginscherm, maar dan zonder de grenzen van een
 * kolom van 320 px: `?all=true` haalt alles op (SCH-5). Eén poort, één volgorde, één begrip van
 * "dringend" — zou dit scherm zijn eigen lijst opbouwen, dan stond er op het beginscherm vroeg of
 * laat iets anders dan hier, en dan vertrouwt niemand meer het eerste.
 *
 * **Wat de mockup toont en wij niet:** de groep "Voltooid", de voortgangsbalk en de sectie
 * "Stappen". Alle drie om dezelfde reden: de poort kent alleen wat open staat (ADR-0066). Een
 * voltooide taak bestaat er niet, dus is er ook geen noemer voor "3 van 7" en geen groep om in te
 * vallen. Dat staat met reden in `docs/design/deviations.md`.
 */

/** Het icoon per bron, met dezelfde kleurparen als op het beginscherm (spec §2.3). */
const ICON: Record<
  OpenItemSource,
  { icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>; classes: string }
> = {
  reach: { icon: ShieldCheck, classes: 'bg-brand-primary-tint text-brand-primary-dark' },
  forms: { icon: FileText, classes: 'bg-brand-primary-tint text-brand-primary-dark' },
  tasks: { icon: ClipboardCheck, classes: 'bg-brand-orange-tint text-brand-orange-deep' },
  agenda: { icon: CalendarClock, classes: 'bg-brand-dark-blue-tint text-brand-dark-blue' },
};

const BRONNEN: OpenItemSource[] = ['reach', 'forms', 'tasks', 'agenda'];

type Sortering = 'deadline' | 'titel';

/** Is de termijn verstreken? `isUrgent` noemt dat ook dringend, maar het leest anders. */
function verstreken(item: OpenItem, nu: Date): boolean {
  return item.dueAt !== null && new Date(item.dueAt).getTime() < nu.getTime();
}

export function AppTasksPage() {
  const { t } = useTranslation();
  const tabs = useAppTabs();
  const { api } = useSession();

  const [bron, setBron] = useState<OpenItemSource | null>(null);
  const [sortering, setSortering] = useState<Sortering>('deadline');
  const [gekozen, setGekozen] = useState<string | null>(null);

  /*
    `?all=true`: dit scherm **is** de werklijst (SCH-5).

    Een eigen cachesleutel naast die van de kaart, want het is een ander antwoord. Zou het dezelfde
    sleutel zijn, dan zou het openen van dit scherm de kaart op Home stilletjes vullen met
    driehonderd regels.
  */
  const openItems = useQuery({
    queryKey: ['open-items', 'alles'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/me/open-items', {
        params: { query: { all: true } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as OpenItemList).items;
    },
  });

  const alles = useMemo(() => openItems.data ?? [], [openItems.data]);

  /** De tellers per bron komen uit de lijst zelf — nooit uit een tweede getal van de server. */
  const perBron = useMemo(() => {
    const telling = new Map<OpenItemSource, number>();
    for (const item of alles) telling.set(item.source, (telling.get(item.source) ?? 0) + 1);
    return telling;
  }, [alles]);

  const zichtbaar = useMemo(() => {
    const gefilterd = bron ? alles.filter((item) => item.source === bron) : alles;
    if (sortering === 'titel') {
      return [...gefilterd].sort((a, b) => a.title.localeCompare(b.title, 'nl'));
    }
    // De server sorteert al op termijn (ADR-0066); deze volgorde is die van de poort.
    return gefilterd;
  }, [alles, bron, sortering]);

  const dringend = zichtbaar.filter((item) => item.urgent);
  const binnenkort = zichtbaar.filter((item) => !item.urgent);

  const geselecteerd = zichtbaar.find((item) => item.key === gekozen) ?? zichtbaar[0] ?? null;

  const nu = new Date();

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="checklists"
      renderLink={renderAppTabLink}
      aside={alles.length > 0 ? <TakenAside items={alles} selected={geselecteerd} nu={nu} /> : null}
    >
      <div data-testid="my-tasks" className="flex flex-col gap-element">
        {/* §2.1 — de sectiekop met de oranje pill. De teller telt de regels. */}
        <div className="flex items-center gap-2">
          <h1 className="font-heading text-section font-semibold text-text-primary">
            {t('myTasks.title')}
          </h1>
          {alles.length > 0 && (
            <span
              data-testid="my-tasks-count"
              className="rounded-pill bg-brand-orange px-2 py-0.5 font-heading text-micro font-bold text-text-on-dark"
            >
              {t('myTasks.open', { count: alles.length })}
            </span>
          )}
        </div>

        {/*
          §2.2 — de werkbalk.

          De mockup filtert op "Alle 7 · Open 4 · Voltooid 3". Zonder voltooide taken betekenen
          "Alle" en "Open" hetzelfde, en dan staan er twee knoppen die niets onderscheiden. In de
          plaats staat hier een filter per **bron**, met dezelfde vorm en dezelfde tellers - dat is
          wat op dit scherm wel iets betekent.
        */}
        <Card compact data-testid="my-tasks-toolbar" className="flex flex-wrap gap-3 px-3.5 py-3">
          <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
            <FilterPill
              label={t('myTasks.all')}
              count={alles.length}
              active={bron === null}
              onClick={() => setBron(null)}
              testId="filter-all"
            />
            {BRONNEN.filter((kandidaat) => (perBron.get(kandidaat) ?? 0) > 0).map((kandidaat) => (
              <FilterPill
                key={kandidaat}
                label={t('myTasks.source_' + kandidaat)}
                count={perBron.get(kandidaat) ?? 0}
                active={bron === kandidaat}
                onClick={() => setBron(bron === kandidaat ? null : kandidaat)}
                testId={'filter-' + kandidaat}
              />
            ))}
          </div>

          {/*
            Twee sorteringen, en allebei uitvoerbaar: op termijn (de volgorde van de poort) en op
            titel. De mockup toont alleen "Sorteer: deadline"; een knop die één waarde toont en
            niets kan wisselen, is een plaatje van een knop.
          */}
          <button
            type="button"
            data-testid="my-tasks-sort"
            onClick={() => setSortering(sortering === 'deadline' ? 'titel' : 'deadline')}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-pill border border-border-light bg-surface px-3.5 py-[7px] font-heading text-caption font-medium text-text-secondary shadow-sm transition-colors hover:border-brand-primary-tint-deep hover:text-brand-primary"
          >
            {t('myTasks.sortBy', {
              what: sortering === 'deadline' ? t('myTasks.sortDeadline') : t('myTasks.sortTitle'),
            })}
            <ChevronDown aria-hidden className="h-3.5 w-3.5" />
          </button>
        </Card>

        {/* §2.3 — de lijst. */}
        {openItems.isPending ? (
          <LoadingState rows={5} label={t('common.loading')} />
        ) : alles.length === 0 ? (
          <Card data-testid="my-tasks-empty" className="py-10 text-center">
            <p className="font-heading text-card-title font-semibold text-text-primary">
              {t('myTasks.emptyTitle')}
            </p>
            <p className="mt-1 text-small text-text-muted">{t('myTasks.emptyHint')}</p>
          </Card>
        ) : (
          <Card compact className="flex flex-col gap-0.5 px-2 pb-2 pt-1">
            {/* Een groep zonder punten verdwijnt met zijn label en zijn teller. */}
            <Groep label={t('myTasks.urgent')} items={dringend} testId="group-urgent">
              {(item) => (
                <TaakRij
                  key={item.key}
                  item={item}
                  nu={nu}
                  selected={geselecteerd?.key === item.key}
                  onSelect={() => setGekozen(item.key)}
                />
              )}
            </Groep>
            <Groep label={t('myTasks.soon')} items={binnenkort} testId="group-soon">
              {(item) => (
                <TaakRij
                  key={item.key}
                  item={item}
                  nu={nu}
                  selected={geselecteerd?.key === item.key}
                  onSelect={() => setGekozen(item.key)}
                />
              )}
            </Groep>
          </Card>
        )}
      </div>
    </DeskShell>
  );
}

function Groep({
  label,
  items,
  testId,
  children,
}: {
  label: string;
  items: OpenItem[];
  testId: string;
  children: (item: OpenItem) => ReactNode;
}) {
  if (items.length === 0) return null;
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 px-3 pb-1 pt-3">
        <span className="font-heading text-micro font-medium uppercase tracking-[0.1em] text-text-muted">
          {label}
        </span>
        <span className="rounded-pill bg-brand-dark-blue-tint px-[7px] py-px font-heading text-micro font-semibold tracking-normal text-brand-dark-blue">
          {items.length}
        </span>
      </div>
      <div className="flex flex-col gap-0.5">{items.map((item) => children(item))}</div>
    </div>
  );
}

function FilterPill({
  label,
  count,
  active,
  onClick,
  testId,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 font-heading text-caption transition-colors',
        active
          ? 'bg-brand-primary-tint font-semibold text-brand-primary-dark'
          : 'font-medium text-text-secondary hover:bg-background hover:text-brand-primary',
      )}
    >
      {label}
      <span className="font-semibold opacity-70">{count}</span>
    </button>
  );
}

/**
 * Eén taakrij (§2.3).
 *
 * **De afvinkcirkel vinkt niet af, en dat is een keuze.** Een bericht bevestig je, een formulier
 * vul je in, een checklist vink je per item af — geen van drieën is "afvinken in een lijst". En
 * bevestigen is onomkeerbaar: een cirkel die bij de ene rij navigeert en bij de andere een
 * handtekening zet, zet die handtekening ooit per ongeluk. De cirkel is hier dus een markering die
 * bij de rij hoort; de rij zelf vult de detailkaart, en de knop daarin brengt je naar de plek waar
 * de taak wel afgehandeld wordt.
 */
function TaakRij({
  item,
  nu,
  selected,
  onSelect,
}: {
  item: OpenItem;
  nu: Date;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const { icon: Icon, classes } = ICON[item.source];
  const teLaat = verstreken(item, nu);

  return (
    <button
      type="button"
      data-testid="task-row"
      data-source={item.source}
      data-urgent={item.urgent ? 'ja' : 'nee'}
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 rounded-md p-3 text-left transition-colors',
        selected ? 'bg-brand-primary-tint' : 'hover:bg-background',
      )}
    >
      <span
        aria-hidden
        data-testid="task-check"
        className="h-[22px] w-[22px] shrink-0 rounded-pill border-2 border-[#C9D1DC] bg-surface"
      />
      <span
        aria-hidden
        data-testid="task-icon"
        className={cn(
          'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md',
          classes,
        )}
      >
        <Icon className="h-4 w-4" />
      </span>

      <span className="min-w-0 flex-1 leading-[1.3]">
        <span
          data-testid="task-row-title"
          className="block truncate font-heading text-copy font-medium text-text-primary"
        >
          {item.title}
        </span>
        <span
          data-testid="task-row-meta"
          className={cn(
            'block truncate text-caption',
            item.urgent ? 'font-heading font-semibold text-brand-orange-deep' : 'text-text-muted',
          )}
        >
          {teLaat ? t('myTasks.overdue') + ' · ' + item.meta : item.meta}
        </span>
      </span>

      {/* Het afdelingslabel van de afzender; onder 640 px valt het weg (spec §5). */}
      {item.sender?.department ? (
        <span
          data-testid="task-row-department"
          className="hidden shrink-0 rounded-pill bg-background px-2 py-0.5 text-micro text-text-muted sm:inline"
        >
          {item.sender.department}
        </span>
      ) : null}

      <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0 text-text-muted" />
    </button>
  );
}

/**
 * De rechterkolom (§3).
 *
 * De kaart "Voortgang" verliest haar balk en haar "3 van 7": zonder voltooide taken is er geen
 * noemer. Wat overblijft zijn twee tellers die wel uit gegevens komen — Open en Dringend — in
 * dezelfde tegelvorm als in de mockup.
 */
function TakenAside({
  items,
  selected,
  nu,
}: {
  items: OpenItem[];
  selected: OpenItem | null;
  nu: Date;
}) {
  const { t } = useTranslation();
  const dringend = items.filter((item) => item.urgent).length;

  return (
    <>
      <Card compact data-testid="my-tasks-counts" className="p-[18px]">
        <div className="grid grid-cols-2 gap-2">
          <Tegel getal={items.length} label={t('myTasks.countsOpen')} />
          <Tegel getal={dringend} label={t('myTasks.countsUrgent')} urgent />
        </div>
      </Card>

      {selected ? <TaakDetail item={selected} nu={nu} /> : null}
    </>
  );
}

function Tegel({ getal, label, urgent }: { getal: number; label: string; urgent?: boolean }) {
  return (
    <div className="rounded-md bg-background px-1.5 py-2.5 text-center">
      <div
        className={cn(
          'font-heading text-card-title font-bold',
          urgent ? 'text-brand-orange-deep' : 'text-text-primary',
        )}
      >
        {getal}
      </div>
      <div className="text-micro text-text-muted">{label}</div>
    </div>
  );
}

/** §3.2 — de detailkaart, zonder de sectie "Stappen". */
function TaakDetail({ item, nu }: { item: OpenItem; nu: Date }) {
  const { t } = useTranslation();
  const { icon: Icon, classes } = ICON[item.source];
  const teLaat = verstreken(item, nu);

  return (
    <Card compact data-testid="task-detail" className="p-[18px]">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className={cn(
            'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md',
            classes,
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="font-heading text-card-title font-semibold leading-[1.3] text-text-primary">
            {item.title}
          </p>
          <p
            className={cn(
              'text-caption',
              item.urgent ? 'font-heading font-semibold text-brand-orange-deep' : 'text-text-muted',
            )}
          >
            {teLaat ? t('myTasks.overdue') + ' · ' + item.meta : item.meta}
          </p>
        </div>
      </div>

      {/* Niets in plaats van een plaatshouder: een module die geen omschrijving heeft, hoort er
          geen te krijgen (SCH-5). */}
      {item.description ? (
        <p
          data-testid="task-detail-desc"
          className="mt-3 text-caption leading-[1.5] text-text-secondary"
        >
          {item.description}
        </p>
      ) : null}

      {/* De bronregel noemt de afzender, nooit een ontvanger (ADR-0014). */}
      {item.sender ? (
        <p
          data-testid="task-detail-source"
          className="mt-2.5 flex items-center gap-2 text-micro text-text-muted"
        >
          {item.sender.department ? (
            <span className="rounded-pill bg-background px-2 py-0.5">{item.sender.department}</span>
          ) : null}
          <span>
            {item.sender.name}
            {item.sender.at
              ? ' · ' +
                relativeTime(item.sender.at, nu, {
                  justNow: t('feed.justNow'),
                  minutesAgo: t('feed.minutesAgo'),
                  hoursAgo: t('feed.hoursAgo'),
                  yesterday: t('feed.yesterday'),
                })
              : ''}
          </span>
        </p>
      ) : null}

      {/*
        Eén knop, en geen "Bekijk bericht ›" ernaast: die link uit de mockup leidt naar dezelfde
        plek als de knop. Twee knoppen naar hetzelfde scherm zijn geen keuze maar ruis.
      */}
      <Link
        {...openItemLink(item.href)}
        data-testid="task-detail-action"
        className={cn(
          'mt-3.5 flex w-full items-center justify-center rounded-pill px-3.5 py-[9px] font-heading text-caption font-semibold transition-colors',
          item.urgent
            ? 'bg-brand-orange text-text-on-dark hover:bg-brand-orange-deep'
            : 'bg-brand-primary-tint text-brand-primary-dark hover:bg-brand-primary hover:text-text-on-dark',
        )}
      >
        {item.actionLabel ?? t('myTasks.open_action')}
      </Link>
    </Card>
  );
}
